import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom';
import { UserService } from '../../../Component/Api/User/UserService';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { IBulkAction } from '../../../Component/Common/Components/DataTable/BulkAction';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { UserDto } from '../../../Component/Model/Dto/User';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { UserManagementModal } from './UserManagementModal';
import { MapToUserManagementTable } from './UserManagementTable';
import { Button, Grid } from '@mui/material';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDAlert } from '../../../Component/Common/Components/MDAlert';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { DataImportType } from '../../../Component/Model/Enum/BEE123Enums';
import { Export } from '../../../Component/Model/Dto/Export';
import { toast } from 'react-toastify';
import { CoreExportService } from '../../../Component/Api/Export/CoreExportService';
import { useRecoilValue } from 'recoil';
import { UserAtom } from '../../../State/user.Atom';

interface IProps {
    ignoreClientUser?: boolean;
    onlyClientUser?: boolean;
}

export const UserManagement = (props: IProps) => {

    const history = useHistory();
    const currentUser = useRecoilValue(UserAtom);
    
    const [userData, setUserData] = React.useState<UserDto.UserRowData[]>([]);
    const [isTableLoading, setIsTableLoading] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const [showUserManagementModal, setShowUsermanagementModal] = React.useState(false);
    const [selectedUserId, setSelectedUserId] = React.useState<string | undefined>(undefined);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(false);

    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [searchValue, setSearchValue] = React.useState<string>("");

    const userSearchRequest: UserDto.Request = {
        columnSortOptions: [],
        ignoreClientUser: props.ignoreClientUser,
        onlyClientUser: props.onlyClientUser,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const onCreateUserClick = () => {
        setShowUsermanagementModal(true);
    };

    const bulkActions: IBulkAction[] = [
        {
            label: 'Create New User',
            action: onCreateUserClick
        }
    ];

    const fetchData = async () => {
        setIsTableLoading(true);
        try {
            let response = await UserService.searchUsers(userSearchRequest);
            setUserData(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsTableLoading(false);
        }
    }

    const exportScorecardUserAccessData = async () => {
        exportData(DataImportType.ScorecardUserAccess);
    }

    const exportUserManagementData = async () => {
        exportData(DataImportType.UserManagement);
    }

    const exportUserRoles = async () => {
        exportData(DataImportType.UserRoles);
    }

    const exportData = async (dataImportType: DataImportType) => {
        const request: Export.FileExportDto = {
            DataImportType: dataImportType,
            parameters: [
                { key: 'IgnoreClientUser', value: props.ignoreClientUser },
                { key: 'OnlyClientUser', value: props.onlyClientUser }
            ]
        };

        try {
            let response = await CoreExportService.ExportFile(request);
            const redirectUrl = response.data;
            window.open(redirectUrl, '_blank');
        }
        catch(error) {
            console.log(error);
            toast.error("Export could not be completed");
        }
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue]);

    const closeUserManagememtModal = () => {
        setShowUsermanagementModal(false);
        setSelectedUserId(undefined);
        fetchData();
    }

    const handleEdit = (id: string) => {
        setSelectedUserId(id);
        setShowUsermanagementModal(true)
    }

    const handleDelete = (id: string) => {
        setShowConfirmationDialog(true);
        setSelectedUserId(id);
    }

    const handleCloseConfirmation = () => {
        setSelectedUserId(undefined);
        setShowConfirmationDialog(false);
    }

    const onDelete = async () => {
        setIsDeleting(true);
        try {
            await UserService.deleteUser(selectedUserId!);
            fetchData();
        } catch (error) {
            console.log(error);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmation();
        }
    }

    const handleUserRoles = (id: string) => {
        history.push(`/userRoles/${id}`);
    }

    const userManagementTableData = MapToUserManagementTable(userData, handleEdit, handleUserRoles, handleDelete);

    return (
        <>
            <DataTable
                table={userManagementTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isTableLoading}
                canSearch={true}
                searchValueChanged={searchValueChanged}
                bulkActions={bulkActions}
            ></DataTable>

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={onDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Delete Entry"}
                message={"Are you sure you want to delete the selected entry?"}
                isLoading={isDeleting}
            />
            <UserManagementModal 
                showUserManagementModal={showUserManagementModal} 
                userId={selectedUserId}
                showOnlyClientUserTypes={props.onlyClientUser ?? false}
                closeUserManagementModal={closeUserManagememtModal} 
                onCreateFinished={() => {
                    closeUserManagememtModal();
                    fetchData();
                }} />

            {currentUser.isAdmin && (
                <Grid container >
                    <Grid item xs={12} lg={12}>
                        <MDBox mt={4}>
                            <MDAlert color='light'>
                                <MDTypography 
                                    component='span' 
                                    variant='body' 
                                    fontSize='0.93rem' 
                                    fontWeight='regular'>
                                    <p style={{ marginBottom: "0.2rem" }}> 
                                        Click<Button 
                                            variant="text" 
                                            onClick={exportUserManagementData} 
                                            size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                        to export users to a file
                                    </p>
                                </MDTypography>
                            </MDAlert>
                            <MDAlert color='light'>
                                <MDTypography 
                                    component='span' 
                                    variant='body' 
                                    fontSize='0.93rem' 
                                    fontWeight='regular'>
                                    <p style={{ marginBottom: "0.2rem" }}> 
                                        Click<Button 
                                            variant="text" 
                                            onClick={exportScorecardUserAccessData} 
                                            size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                        to export scorecard's user access to a file
                                    </p>
                                </MDTypography>
                            </MDAlert>
                            <MDAlert color='light'>
                                <MDTypography 
                                    component='span' 
                                    variant='body' 
                                    fontSize='0.93rem' 
                                    fontWeight='regular'>
                                    <p style={{ marginBottom: "0.2rem" }}> 
                                        Click<Button 
                                            variant="text" 
                                            onClick={exportUserRoles} 
                                            size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                        to export users and their roles
                                    </p>
                                </MDTypography>
                            </MDAlert>
                        </MDBox> 
                    </Grid>
                </Grid>   
            )}
        </>
    )
}
